// src/containers/pages/caseManagement/components/CasePicker.tsx

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  MuiToggleButton as ToggleButton,
  Typography,
  Tooltip,
} from 'components/mui';
import getCases from 'api/comments/getCases';
import type { CANtrackCaseRaw } from 'api/comments/getCases';
import type { SelectedCustomer } from '../types';

interface CaseOption {
  Id: number;
  Title: string;
}

interface CasePickerProps {
  label: string; // "Source Case" or "Destination Case"
  customer: SelectedCustomer;
  onSelectCase: (caseId: number | null) => void;
  disabled?: boolean;
  disabledReason?: string;
  currentCaseId?: number; // ID of the currently loaded case (to highlight)
  headerAction?: React.ReactNode; // right-aligned slot in the header row (source panel only)
  resetSignal?: number; // bump to clear the picker's own selection (e.g. after the case is deleted)
}

type PickerMode = 'search' | 'id';

function CasePicker({
  label,
  customer,
  onSelectCase,
  disabled,
  disabledReason,
  currentCaseId,
  headerAction,
  resetSignal,
}: CasePickerProps) {
  const [mode, setMode] = useState<PickerMode>('search');
  const [caseOptions, setCaseOptions] = useState<CaseOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<CaseOption | null>(null);
  const [caseIdInput, setCaseIdInput] = useState('');
  const [loading, setLoading] = useState(false);

  // The picked case lives in this component's own state, so a parent that drops
  // the loaded case (a successful delete) cannot clear the search field on its
  // own - it bumps resetSignal instead (FE-DEFECT-2, 2026-07-21). Mode is left
  // alone deliberately: the user's Search/Case ID choice survives the reset.
  const lastResetSignal = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal === lastResetSignal.current) return; // mount / unrelated re-render
    lastResetSignal.current = resetSignal;
    setCaseOptions([]);
    setSelectedOption(null);
    setCaseIdInput('');
  }, [resetSignal]);

  const handleModeChange = useCallback(
    (_e: unknown, newMode: PickerMode | null) => {
      if (!newMode) return;
      setMode(newMode);
      // Clear the other mode's state
      setCaseOptions([]);
      setSelectedOption(null);
      setCaseIdInput('');
    },
    [],
  );

  // Search cases by keyword for a specific customer
  const handleCaseSearch = useCallback(
    async (keyword: string) => {
      try {
        setLoading(true);
        const filterPayload = {
          SearchKeyword: keyword,
          Page: 1,
          Take: 200,
          CustomerId: customer.id,
        };
        const result = await getCases(filterPayload);
        const cases: CaseOption[] = (result.rawCases || []).map(
          (c: CANtrackCaseRaw) => ({
            Id: parseInt(c.Id),
            Title: c.Title,
          }),
        );
        setCaseOptions(cases);
      } catch (error) {
        console.error('[CasePicker] Case search failed:', error);
        setCaseOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [customer.id],
  );

  // Select from dropdown
  const handleDropdownSelect = useCallback(
    (_e: unknown, val: CaseOption | null) => {
      setSelectedOption(val);
      onSelectCase(val ? val.Id : null);
    },
    [onSelectCase],
  );

  // Submit Case ID
  const handleIdSubmit = useCallback(() => {
    const id = parseInt(caseIdInput, 10);
    if (!isNaN(id) && id > 0) {
      onSelectCase(id);
    }
  }, [caseIdInput, onSelectCase]);

  const handleIdKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleIdSubmit();
      }
    },
    [handleIdSubmit],
  );

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          disabled={disabled}
        >
          <ToggleButton value="search" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>
            Search
          </ToggleButton>
          <ToggleButton value="id" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>
            Case ID
          </ToggleButton>
        </ToggleButtonGroup>
        {headerAction ? <Box sx={{ ml: 'auto' }}>{headerAction}</Box> : null}
      </Box>

      {mode === 'search' ? (
        <Autocomplete
          options={caseOptions}
          getOptionLabel={(opt: CaseOption) =>
            `#${opt.Id} - ${opt.Title}`
          }
          value={selectedOption}
          onChange={handleDropdownSelect}
          onInputChange={(_e: unknown, val: string) => handleCaseSearch(val)}
          onOpen={() => handleCaseSearch('')}
          isOptionEqualToValue={(opt: CaseOption, val: CaseOption) => opt.Id === val.Id}
          loading={loading}
          disabled={disabled}
          renderInput={(params: any) => (
            <TextField
              {...params}
              label={`Search ${label.toLowerCase()}...`}
              size="small"
            />
          )}
        />
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Case ID"
            value={caseIdInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCaseIdInput(e.target.value)
            }
            onKeyDown={handleIdKeyDown}
            onBlur={handleIdSubmit}
            size="small"
            type="number"
            disabled={disabled}
            sx={{ flex: 1 }}
          />
        </Box>
      )}

      {currentCaseId && (
        <Typography variant="caption" color="text.secondary">
          Loaded: Case #{currentCaseId}
        </Typography>
      )}
    </Box>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} arrow>
        <Box>{content}</Box>
      </Tooltip>
    );
  }

  return content;
}

export default CasePicker;
